import { SingleIndex } from '@deqo/foundation';
import { ClientRef, EventSourceBase, KeyedSet, PresenceEvents } from '@deqo/podopolis';
import { EditorPodSession } from '../../app/canvas/EditorPodSession';
import { Client } from './Client';
import { Id, Manager, Pod, UserId } from './ClientSymbols';
import { Self } from './Self';

export interface ClientManagerStreams {
    join: Client;
    leave: Client;
    update: [Client, string[]];
}

const emptyDerivatives = () => ({
    self: null as Self | null,
    others: [] as Client[],
    all: [] as Client[]
});

export class ClientManager extends EventSourceBase<ClientManagerStreams> {

    private clientsById = new KeyedSet<Client>(x => x.id);
    private clientsByUser = new SingleIndex<string, Client>();
    private derivatives = emptyDerivatives();

    constructor(private pod: EditorPodSession) {
        super();
        this.init();
        this.burden(pod.on('clientJoin', this.onClientJoin));
        this.burden(pod.on('clientLeave', this.onClientLeave));
        this.burden(pod.on('clientUpdate', this.onClientUpdate));
    }

    public get self() {
        return this.derivatives.self!;
    }

    public get others() {
        return this.derivatives.others;
    }

    public get all() {
        return this.derivatives.all;
    }

    public get(id: string) {
        return this.clientsById.get(id) ?? null;
    }

    public getForUser(userId: string) {
        return this.clientsByUser.get(userId);
    }

    private init() {
        const clients = this.pod.getClients();
        for (const ref of clients) {
            this.allocateClient(ref);
        }
        this.updateDerivatives();
    }

    private allocateClient(ref: ClientRef) {
        const self = ref.id === this.pod.getThisClient().id;
        const client = self ? new Self() : new Client();
        client[Id] = ref.id;
        client[UserId] = ref.userId;
        client[Manager] = this;
        client[Pod] = this.pod;
        this.clientsById.add(client);
        this.clientsByUser.add(client.userId, client);
        this.updateDerivatives();
        return client;
    }

    private releaseClient(ref: ClientRef) {
        const client = this.clientsById.get(ref.id);
        this.clientsById.remove(client);
        this.clientsByUser.remove(client.userId, client);
        this.updateDerivatives();
        return client;
    }

    private updateDerivatives() {
        const deri = this.derivatives = emptyDerivatives();
        for (const client of this.clientsById) {
            if (client instanceof Self) {
                deri.self = client;
            }
            else {
                deri.others.push(client);
            }
            deri.all.push(client);
        }
    }

    private onClientJoin = (ref: ClientRef) => {
        this.emit('join', this.allocateClient(ref));
    };

    private onClientLeave = (ref: ClientRef) => {
        this.emit('leave', this.releaseClient(ref));
    };

    private onClientUpdate = ([ref, _profile, affectedKeys]: PresenceEvents['clientUpdate']) => {
        this.emit('update', [this.clientsById.get(ref.id), affectedKeys]);
    };
}
